import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, verifyMagicLink } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Loading } from "../components/States";

/** Magic-link landing route. Reads ?token= from the URL, POSTs /auth/verify (the
 *  worker sets the httpOnly ac_portal cookie on success), then routes into the
 *  portal. The token is consumed once; refreshing this page will fail by design. */
export function VerifyPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const location = useLocation();
  // Where to land after sign-in: the page the user was originally headed to
  // (threaded through SignInPage's state), defaulting to their requests.
  const from = (location.state as { from?: string } | null)?.from ?? "/tickets";
  const { markAuthenticated } = useAuth();

  const [error, setError] = useState<string | null>(null);
  // StrictMode double-invokes effects in dev; the token is single-use, so guard
  // against issuing the verify request twice.
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setError("This sign-in link is missing its token. Request a new link to continue.");
      return;
    }

    let active = true;
    verifyMagicLink(token)
      .then((res) => {
        if (!active) return;
        if (res.ok) {
          markAuthenticated();
          navigate(from, { replace: true });
        } else {
          setError("This sign-in link is invalid or has expired.");
        }
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError && err.status === 401) {
          setError("This sign-in link is invalid or has already been used. Request a new one.");
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError("We couldn’t verify your sign-in link. Please try again.");
        }
      });
    return () => {
      active = false;
    };
  }, [token, navigate, markAuthenticated, from]);

  if (error) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <h1>Sign-in link problem</h1>
          <p className="lead">{error}</p>
          <Link to="/signin" className="btn primary" style={{ width: "100%" }}>
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return <Loading label="Signing you in…" />;
}
